import { Injectable } from '@angular/core';
import { Crate } from '../../../models/Crate';
import { MapTooltipContent, MapTooltipLevelType, MapTooltipPalletLevel } from 'src/app/pages/map/map-tooltip/map-tooltip.types';
import { Level } from 'src/app/modals/stock-levels-model/stock-levels-modal.component';
import { IMapTooltipService } from './map-tooltip.interface';
import { findLevelByZ } from '../../helpers/find-crate-level';
import { GroundType } from 'src/app/pages/map/warehouse-map-task-position-view/ground-cell';

type ExtendedLevel = Level & { isTenantLevel: boolean };

@Injectable({
  providedIn: 'root'
})
export class CrateTooltipService implements IMapTooltipService {
  private palletsBy = new Map<string, Map<number, MapTooltipPalletLevel>>();

  constructor() {}

  public processCratesAndLevels(crates: Crate[], levelsByXY: Map<string, Level[]>): void {
    for (const crate of crates) {
      const idx = `${crate.x}_${crate.y}`;
      const levels = (levelsByXY.get(idx) || []).map(level => ({ ...level, isTenantLevel: false }));
      
      if (!this.palletsBy.has(idx)) {
        this.palletsBy.set(idx, this.prepareEmptyTooltipLevels(levels));
      }

      const level = findLevelByZ(crate.z, levels) as ExtendedLevel | undefined;
      this.addPalletToLevel(idx, level, crate);
    }
  }

  private prepareEmptyTooltipLevels(levels: ExtendedLevel[]): Map<number, MapTooltipPalletLevel> {
    const tooltipLevels = new Map<number, MapTooltipPalletLevel>();
    
    // Add regular levels
    for (const level of levels) {
      tooltipLevels.set(level.order, {
        order: level.order,
        groundGroup: level.type,
        type: MapTooltipLevelType.REGULAR,
        name: level.name,
        pallets: [],
        palletQty: 0
      });
    }
    
    // Add unknown level
    if (!tooltipLevels.has(0)) {
      tooltipLevels.set(0, {
        order: 0,
        type: MapTooltipLevelType.UNKNOWN,
        name: 'indefinido',
        pallets: [],
        palletQty: 0
      });
    }
    
    return tooltipLevels;
  }

  private addPalletToLevel(positionIdx: string, level: ExtendedLevel | undefined, crate: Crate): void {
    const order = level?.order || 0;
    const pallet = {
      name: `Pallet ${order}`,
      products: crate.skuGroups.map(group => ({
        sku: group.sku,
        quantity: group.quantity * (group.packSize || 1)
      })),
      skuQty: crate.skuGroups.length
    };
    
    const tooltipLevel = this.palletsBy.get(positionIdx)?.get(order);
    if (tooltipLevel) {
      tooltipLevel.pallets.push(pallet);
      tooltipLevel.palletQty += 1;
    }
  }

  public getTooltipContent(x: number, y: number, groundPositionType?: string): MapTooltipContent {
    const idx = `${x}_${y}`;
    const isPigeonhole = groundPositionType === GroundType.PIGEONHOLE;
    const positionInfo = this.palletsBy.get(idx);
    if (positionInfo != undefined) {
      const palletsByLevels: MapTooltipPalletLevel[] = Array.from(positionInfo.values());
      
      const palletsByLevelsOrdered = palletsByLevels.sort((a, b) => b.order - a.order).map(level => ({
        ...level,
        pallets: level.pallets.slice(0, 2)
      }));
      return { levels: palletsByLevelsOrdered, empty: false, isPigeonhole };
    }
    return { levels: [], empty: true, isPigeonhole };
  }
} 